import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, FileDown, Eye } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const PRIMARY = '#10b981';
const PRIMARY_HOVER = '#059669';
const PRIMARY_LIGHT = '#ECFDF5';
const BG = '#F8FAFC';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#1E293B';
const TEXT_MUTED = '#64748B';
const BORDER = '#E5E7EB';

// 60 hours = 4 hr/day × 15 days. 15 Dec 2025 to 29 Dec 2025.
const START_DATE = new Date(2025, 11, 15); // Dec 15, 2025
const DAYS_COUNT = 15;
const HOURS_PER_DAY = 4;

function getAttendanceDates(): { date: Date; dayName: string; hours: number }[] {
  const rows: { date: Date; dayName: string; hours: number }[] = [];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  for (let i = 0; i < DAYS_COUNT; i++) {
    const d = new Date(START_DATE);
    d.setDate(d.getDate() + i);
    rows.push({
      date: d,
      dayName: dayNames[d.getDay()],
      hours: HOURS_PER_DAY,
    });
  }
  return rows;
}

const ATTENDANCE_ROWS = getAttendanceDates();

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

type FormState = {
  universityRollNo: string;
  universityRegNo: string;
  name: string;
  fatherName: string;
  department: string;
};

const initialForm: FormState = {
  universityRollNo: '',
  universityRegNo: '',
  name: '',
  fatherName: '',
  department: '',
};

const AttendanceGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const documentRef = useRef<HTMLDivElement>(null);

  const update = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePreview = () => {
    if (!documentRef.current) return;
    const printContent = documentRef.current.innerHTML;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Attendance - ${form.name || 'Preview'}</title>
          <style>
            body { font-family: 'Inter', 'Segoe UI', sans-serif; padding: 24px; color: #1E293B; }
            .header { text-align: center; border-bottom: 2px solid #10b981; padding-bottom: 12px; margin-bottom: 20px; }
            .header h1 { margin: 0; font-size: 1.5rem; color: #059669; }
            .header p { margin: 4px 0 0; font-size: 0.875rem; color: #64748B; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 0.875rem; }
            th, td { border: 1px solid #E5E7EB; padding: 10px 12px; text-align: left; }
            th { background: #ECFDF5; color: #065F46; font-weight: 600; }
            .footer { margin-top: 28px; border-top: 1px solid #E5E7EB; padding-top: 16px; font-size: 0.8rem; color: #64748B; text-align: center; }
            .signature { margin-top: 32px; text-align: right; }
            .signature-line { border-top: 1px solid #1E293B; width: 200px; margin-left: auto; margin-top: 40px; padding-top: 4px; font-size: 0.8rem; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  const handleDownloadPDF = async () => {
    if (!documentRef.current) return;
    try {
      const canvas = await html2canvas(documentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4', true);
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfW / canvas.width, pdfH / canvas.height) * 0.95;
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      const x = (pdfW - imgW) / 2;
      pdf.addImage(imgData, 'PNG', x, 10, imgW, imgH);
      pdf.save(`Attendance_${form.name || 'Intern'}_${form.universityRollNo || 'NA'}.pdf`);
    } catch (err) {
      console.error('PDF generation failed', err);
    }
  };

  const canGenerate = form.name.trim() && form.universityRollNo.trim() && form.department.trim();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: BG,
        pt: { xs: 2, md: 8 },
        pb: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
        <Button
          startIcon={<ChevronLeft size={18} />}
          onClick={() => navigate('/gen-ai-course')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: TEXT_DARK,
            mb: 2,
            '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY },
          }}
        >
          Back to AI for Everyone
        </Button>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            fontWeight: 700,
            color: TEXT_DARK,
            mb: 1,
          }}
        >
          Generate 60-Hour Attendance
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, mb: 3, fontSize: '0.95rem' }}>
          Fill in your details to generate attendance (4 hours per day, 15 Dec 2025 – 29 Dec 2025).
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            bgcolor: CARD_BG,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 2 }}>Student / Intern Details</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="University Roll No"
              value={form.universityRollNo}
              onChange={update('universityRollNo')}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="University Registration No"
              value={form.universityRegNo}
              onChange={update('universityRegNo')}
              fullWidth
              size="small"
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={update('name')}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Father's Name"
              value={form.fatherName}
              onChange={update('fatherName')}
              fullWidth
              size="small"
            />
            <TextField
              label="Department"
              value={form.department}
              onChange={update('department')}
              fullWidth
              size="small"
              required
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.9rem', color: TEXT_MUTED }}>
                <strong style={{ color: TEXT_DARK }}>Course Duration:</strong> 60 hours
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: TEXT_MUTED }}>
                <strong style={{ color: TEXT_DARK }}>Course Name:</strong> AI for Everyone
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Eye size={18} />}
            onClick={handlePreview}
            disabled={!canGenerate}
            sx={{
              bgcolor: PRIMARY,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: PRIMARY_HOVER },
              '&:disabled': { bgcolor: '#9CA3AF', color: '#fff' },
            }}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDown size={18} />}
            onClick={handleDownloadPDF}
            disabled={!canGenerate}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: PRIMARY_HOVER, bgcolor: PRIMARY_LIGHT },
              '&:disabled': { borderColor: BORDER, color: TEXT_MUTED },
            }}
          >
            Download PDF
          </Button>
        </Box>

        <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1.5 }}>Attendance table (per day)</Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            overflow: 'auto',
            mb: 3,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: '#065F46' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: '#065F46' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: '#065F46' }}>Day</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: '#065F46' }}>Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ATTENDANCE_ROWS.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell>{row.dayName}</TableCell>
                  <TableCell>{row.hours}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700, color: TEXT_DARK }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{DAYS_COUNT * HOURS_PER_DAY} hours</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Hidden document for PDF/Print - header, footer, signature from letter style */}
        <Box
          ref={documentRef}
          sx={{
            position: 'absolute',
            left: -9999,
            top: 0,
            width: 210 * 3.78,
            maxWidth: '210mm',
            bgcolor: '#fff',
            color: TEXT_DARK,
            p: 3,
            fontFamily: '"Inter", "Segoe UI", sans-serif',
            fontSize: '0.875rem',
          }}
        >
          {/* Header - letter style */}
          <Box sx={{ textAlign: 'center', borderBottom: `2px solid ${PRIMARY}`, pb: 1.5, mb: 2 }}>
            <Typography component="h1" sx={{ m: 0, fontSize: '1.25rem', fontWeight: 700, color: PRIMARY }}>
              Reviving India
            </Typography>
            <Typography sx={{ m: 0, fontSize: '0.8rem', color: TEXT_MUTED }}>
              Internship / Training Attendance Certificate
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Candidate Details</Typography>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: '0.8rem' }}>
            <tbody>
              <tr><td style={{ padding: '4px 8px 4px 0', width: '40%' }}>Name</td><td style={{ padding: 4 }}>{form.name || '—'}</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>Father's Name</td><td style={{ padding: 4 }}>{form.fatherName || '—'}</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>University Roll No</td><td style={{ padding: 4 }}>{form.universityRollNo || '—'}</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>University Registration No</td><td style={{ padding: 4 }}>{form.universityRegNo || '—'}</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>Department</td><td style={{ padding: 4 }}>{form.department || '—'}</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>Course Duration</td><td style={{ padding: 4 }}>60 hours</td></tr>
              <tr><td style={{ padding: '4px 8px 4px 0' }}>Course Name</td><td style={{ padding: 4 }}>AI for Everyone</td></tr>
            </tbody>
          </Box>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Attendance (4 hours per day, 15 Dec 2025 – 29 Dec 2025)</Typography>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #E5E7EB', padding: 8, textAlign: 'left', background: '#ECFDF5' }}>S.No</th>
                <th style={{ border: '1px solid #E5E7EB', padding: 8, textAlign: 'left', background: '#ECFDF5' }}>Date</th>
                <th style={{ border: '1px solid #E5E7EB', padding: 8, textAlign: 'left', background: '#ECFDF5' }}>Day</th>
                <th style={{ border: '1px solid #E5E7EB', padding: 8, textAlign: 'left', background: '#ECFDF5' }}>Hours</th>
              </tr>
            </thead>
            <tbody>
              {ATTENDANCE_ROWS.map((row, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #E5E7EB', padding: 8 }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #E5E7EB', padding: 8 }}>{formatDate(row.date)}</td>
                  <td style={{ border: '1px solid #E5E7EB', padding: 8 }}>{row.dayName}</td>
                  <td style={{ border: '1px solid #E5E7EB', padding: 8 }}>{row.hours}</td>
                </tr>
              ))}
              <tr style={{ background: '#F8FAFC' }}>
                <td colSpan={3} style={{ border: '1px solid #E5E7EB', padding: 8, fontWeight: 700 }}>Total</td>
                <td style={{ border: '1px solid #E5E7EB', padding: 8, fontWeight: 700 }}>{DAYS_COUNT * HOURS_PER_DAY} hours</td>
              </tr>
            </tbody>
          </Box>

          {/* Footer */}
          <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.5, mt: 2, fontSize: '0.75rem', color: TEXT_MUTED, textAlign: 'center' }}>
            This document is generated for internship/training attendance records. Reviving India.
          </Box>

          {/* Signature with image and designation - place sign.jpg in public folder */}
          <Box sx={{ mt: 3, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box
              component="img"
              src="/sign.jpg"
              alt="Signature"
              sx={{
                height: 48,
                width: 'auto',
                objectFit: 'contain',
                mb: 0.5,
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>
              Founder & CEO
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: TEXT_MUTED }}>
              RevivingIndia
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AttendanceGenerator;
